/**
 * Weekly Digest Page
 * Shows firm-wide weekly summary.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, MessageSquare, Users, Link2, TrendingUp, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const fmtBytes = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(0)} MB` : `${(b / 1024).toFixed(0)} KB`;

export default function DigestPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['weekly-digest'],
    queryFn: async () => { const r = await apiClient.get('/manager/digest'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#7c75f0]" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-medium">Weekly Digest</h2>
        <p className="text-sm text-zinc-500">{data.period}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <Users className="h-5 w-5 text-[#7c75f0] mb-2" />
          <p className="text-2xl font-bold">{data.stats.totalClients}</p>
          <p className="text-sm text-zinc-500">Total Clients</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <Link2 className="h-5 w-5 text-green-400 mb-2" />
          <p className="text-2xl font-bold">{data.stats.xeroConnected}</p>
          <p className="text-sm text-zinc-500">Xero Connected</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <FileText className="h-5 w-5 text-amber-400 mb-2" />
          <p className="text-2xl font-bold">{data.stats.newDocuments}</p>
          <p className="text-sm text-zinc-500">Docs This Week</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <MessageSquare className="h-5 w-5 text-blue-400 mb-2" />
          <p className="text-2xl font-bold">{data.stats.conversations}</p>
          <p className="text-sm text-zinc-500">Conversations</p>
        </div>
      </div>

      {/* Highlights */}
      {data.highlights.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">This Week</h3>
          {data.highlights.map((h: string, i: number) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-xl">
              <TrendingUp className="h-4 w-4 text-[#7c75f0] shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-300">{h}</p>
            </div>
          ))}
        </div>
      )}

      {/* Most Active Clients */}
      {data.activeClients?.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Most Active Clients</h3>
          {data.activeClients.map((c: any, i: number) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
              <span className="text-sm">{c.display_name}</span>
              <span className="text-xs text-zinc-500">{c.message_count} messages</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions Needed */}
      {data.actions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Actions Needed</h3>
          {data.actions.map((a: string, i: number) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl">
              <CheckCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-300">{a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
